// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { Button, Colors, Dialog, FooterButton, Shadows } from '@tls/treact-ui';
import { nativeVerticalScrollStyles } from 'components/common/scroll/nativeScrollStyles';
import TestableLoader from 'components/treactui-template/atoms/TestableLoader';
import type { Release, ReleaseChangeLogContent } from 'model/ReleaseInformation';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const categories = ['new_features', 'bug_fixes', 'internal_improvements'] as const;

export default function ReleaseChangeLogDialog({
  onClose,
  onRetry,
  version,
  automatic = false,
  content,
}: Readonly<{
  onClose: () => void;
  onRetry: () => void;
  version?: string;
  automatic?: boolean;
  content: ReleaseChangeLogContent;
}>) {
  const { t, i18n } = useTranslation();
  const title = t(automatic ? 'releaseChangeLog.newVersionTitle' : 'releaseChangeLog.title');
  const dateFormatter = new Intl.DateTimeFormat(i18n.resolvedLanguage ?? 'en', {
    dateStyle: 'long',
    timeZone: 'UTC',
  });

  return (
    <Dialog open size='large' enableClosing onClose={onClose} data-testid='release-change-log-dialog'>
      <Dialog.DialogHeader label={title} showCloseButton />
      <Content className='font-size_m' tabIndex={0} aria-label={title} data-testid='release-change-log-content'>
        {version && (
          <CurrentVersionSection>
            <h2 data-testid='release-change-log-current-version'>
              {t(automatic ? 'releaseChangeLog.newVersion' : 'releaseChangeLog.currentVersion', { version })}
            </h2>
          </CurrentVersionSection>
        )}
        {content.status === 'ready' ? (
          content.releases.map(release => (
            <ReleaseSection key={`${release.version}-${release.release_date}`} data-testid='release-change-log-release'>
              <ReleaseHeading>
                <h2>{release.version}</h2>
                <time dateTime={release.release_date}>
                  {dateFormatter.format(new Date(`${release.release_date}T00:00:00Z`))}
                </time>
              </ReleaseHeading>
              <ReleaseChanges changes={release.change_log} />
            </ReleaseSection>
          ))
        ) : (
          <StatusContent data-testid='release-change-log-status'>
            {content.status === 'loading' ? (
              <output aria-live='polite'>
                <TestableLoader label={t('releaseChangeLog.loading')} data-testid='release-change-log-loader' />
              </output>
            ) : (
              <>
                <p role='alert'>{t(`releaseChangeLog.${content.reason}`)}</p>
                <Button
                  label={t('releaseChangeLog.retry')}
                  onClick={onRetry}
                  data-testid='release-change-log-retry-button'
                />
              </>
            )}
          </StatusContent>
        )}
      </Content>
      <Dialog.Footer
        right={[
          <FooterButton
            key='close'
            label={t('close')}
            variant='secondary'
            onClick={onClose}
            data-testid='release-change-log-close-button'
          />,
        ]}
      />
    </Dialog>
  );
}

function ReleaseChanges({ changes }: Readonly<{ changes: Release['change_log'] }>) {
  const { t } = useTranslation();

  if (categories.every(category => changes[category].length === 0)) {
    return <p>{t('releaseChangeLog.noChanges')}</p>;
  }

  return categories.map(category =>
    changes[category].length ? (
      <div key={category}>
        <h3>{t(`releaseChangeLog.categories.${category}`)}</h3>
        <ul>
          {changes[category].map(change => (
            <li key={change}>{change}</li>
          ))}
        </ul>
      </div>
    ) : null
  );
}

const Content = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex: 1;
  ${nativeVerticalScrollStyles};
  overflow-wrap: anywhere;
  padding: 0.875rem 1.25rem;
  box-shadow: ${Shadows.topEdge};
`;

const StatusContent = styled.div`
  flex: 1;
  min-height: 12rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  text-align: center;

  p {
    margin: 0;
  }
`;

const ReleaseSection = styled.section`
  flex-shrink: 0;
  padding: 1.25rem 0;
  border-top: 1px solid ${Colors.trgrey2.hex};

  h2 {
    margin: 0;
    font-size: 1.25rem;
  }

  h3 {
    margin: 1rem 0 0.5rem;
    font-size: 1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.375rem;
    white-space: pre-wrap;
  }
`;

const CurrentVersionSection = styled(ReleaseSection)`
  border-top: none;

  h2 {
    font-size: 1.5rem;
  }
`;

const ReleaseHeading = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem 1rem;
`;
